import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bed, PlusCircle, Trash2, Edit } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { filterCamas, createCama, updateCama, deleteCama } from '@/services/camas.service';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import CamaFormModal from './components/CamaFormModal';

const CamasPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [prendasParametrizadas, setPrendasParametrizadas] = useState([]);
  const [loadingPrendas, setLoadingPrendas] = useState(false);
  const { toast } = useToast();

  const columns = [
    {
      id: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (row) => (
        <div className="font-medium">{row.nombre}</div>
      )
    },
    {
      id: 'descripcion',
      label: 'Descripción',
      sortable: false,
      render: (row) => (
        <div className="text-sm text-gray-600">{row.descripcion || '-'}</div>
      )
    },
    {
      id: 'capacidad',
      label: 'Capacidad',
      sortable: true,
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {row.capacidad} {row.capacidad === 1 ? 'persona' : 'personas'}
          </span>
        </div>
      )
    },
    {
      id: 'prendas',
      label: 'Prendas',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          {row.prendas && row.prendas.length > 0 ? (
            <span className="text-gray-700">{row.prendas.length} prenda(s)</span>
          ) : (
            <span className="text-gray-400">Sin prendas</span>
          )}
        </div>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.estado === 'Activo' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {row.estado}
        </span>
      )
    },
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'edit',
          icon: 'Edit',
          tooltip: 'Editar cama',
          className: 'hover:bg-blue-100 text-blue-600'
        },
        {
          key: 'delete',
          icon: 'Trash',
          tooltip: 'Eliminar cama',
          className: 'hover:bg-red-100 text-red-600'
        }
      ]
    }
  ];

  const fetchPrendasParametrizadas = useCallback(async () => {
    setLoadingPrendas(true);
    try {
      const response = await getGarmentTypes({
        page: 1,
        limit: 1000, // Cargar todas las prendas activas
        query: { estado: 'Activo' }
      });
      
      const prendasFormateadas = (response.data || []).map(prenda => ({
        id: prenda._id,
        nombre: prenda.nombre,
        peso: prenda.peso / 1000, // Convertir gramos a kilogramos
        descripcion: prenda.descripcion || ''
      }));
      
      setPrendasParametrizadas(prendasFormateadas);
    } catch (error) {
      console.error('Error al cargar prendas parametrizadas:', error);
      toast({
        title: 'Advertencia',
        description: 'No se pudieron cargar las prendas parametrizadas.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPrendas(false);
    }
  }, [toast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await filterCamas({}, itemsPerPage, currentPage);
      setData(Array.isArray(response.data) ? response.data : []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar camas:', error);
      setData([]);
      setTotalRecords(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las camas.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, toast]);

  // Cargar prendas parametrizadas al montar el componente
  useEffect(() => {
    fetchPrendasParametrizadas();
  }, [fetchPrendasParametrizadas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    setCurrentPage(newPage);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateCama(editingItem._id, formData);
        toast({ title: 'Éxito', description: 'Cama actualizada correctamente.' });
      } else {
        await createCama(formData);
        toast({ title: 'Éxito', description: 'Cama creada correctamente.' });
      }
      // Al crear una cama nueva, vamos a la primera página para verla.
      // Como currentPage cambia, el useEffect de fetchData se dispara solo.
      setCurrentPage(1);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo guardar la cama. ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteCama(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Cama eliminada correctamente.' });

      // Calculamos el nuevo total de páginas (un registro menos).
      const newTotalRecords = Math.max(0, totalRecords - 1);
      const newTotalPages = Math.max(1, Math.ceil(newTotalRecords / itemsPerPage));

      // Si la página actual quedó fuera del rango (ej: estabas en la última
      // página y eliminaste su último registro), retrocedemos una página.
      // Si no, mantenemos currentPage y forzamos recarga manual.
      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
      } else {
        await fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo eliminar la cama. ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionKey, row) => {
    if (actionKey === 'edit') {
      handleEdit(row);
    } else if (actionKey === 'delete') {
      handleDeleteClick(row);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <Bed className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Gestión de Camas
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Administra los tipos de camas y sus prendas asociadas
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleCreate}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Nueva Cama
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data}
            isLoading={loading}
            onAction={handleAction}
            emptyMessage="No hay camas registradas"
          />
          
          {totalRecords > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalRecords}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <CamaFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        editingItem={editingItem}
        prendasParametrizadas={prendasParametrizadas}
        loadingPrendas={loadingPrendas}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente la cama "{itemToDelete?.nombre}".
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default CamasPage;
